import {
  Calendar1Icon,
  FileUser,
  GavelIcon,
  type LucideIcon,
  MailIcon,
  MailWarningIcon,
  MessageSquareDotIcon,
  MessageSquareShareIcon,
  MessageSquareText,
  PinIcon,
} from "lucide-react";

import type { ClaimDocuments, ProcedureDocuments } from "@upclaim/sdk/src/data-contracts";

export type ClaimDocument = ClaimDocuments[number];
export type ClaimDocumentType = ClaimDocument["documentType"]["type"];
export type ProcedureDocument = ProcedureDocuments[number];
export type ProcedureDocumentType = ProcedureDocument["type"]["name"];

/** Port of utils/config/constants/documents-types.ts (Vue). */
export const typeTranslations: { [key in ClaimDocumentType]: string } = {
  OTHER: "Autre",
  MED: "Mise en Demeure",
  LEGAL_SUBPOENA_SERVED: "Assignation notifiée",
  LEGAL_SUBPOENA_TO_REVIEW: "Assignation à valider",
  DEBT_CONTRACT: "Cession de créance",
  CLAIM_NOTIFICATION: "Notification cession de créance",
  INVOICES_MERGED: "Factures fusionnées",
  KBIS_UPCLAIM: "KBIS société Upclaim",
  ELIGIBILITY_PROOF: "Preuve d'éligibilité",
  RIB_UPCLAIM: "RIB du dossier",
  FIRST_CONCILIATION_ATTEMPT: "Première tentative de conciliation",
  MED_RECEIPT: "Avis de réception MED",
  MED_NEGLECTED: "Preuve de négligence MED",
  MED_LRE_EMAIL_SENT: "Preuve d'envoi de MED (LRE)",
  ESCP_A: "Formulaire A (ESPC)",
  ESCP_B: "Formulaire B (ESPC)",
  ESCP_C: "Formulaire C (ESPC)",
  ESCP_D: "Formulaire D (ESPC)",
  COURT_DECISION: "Décision de tribunal",
  PLEADING_FILE: "Dossier de plaidoirie",
  KBIS_OPPONENT: "KBIS société adverse",
  ID_PROOF: "Carte(s) d'identité",
  ETICKET: "E-ticket(s)",
  DEBT_CONTRACT_ENTERPRISE: "Cession entreprise envers Upclaim",
  DEBT_CONTRACT_TRAVEL_AGENCY_TO_UPCLAIM: "Cession agence envers Upclaim",
  PROOF_OF_EXPENSE: "Justificatif de dépense",
  RECEIVABLE_TRAIL: "Dossier de preuve Signature",
  BOOKING_CERTIFICATE: "Preuve de réservation",
  PASSENGER_DECLARATION: "Déclaration passager",
  ONE_PAGER: "Résumé du dossier",
  MEDIATION_ONE_PAGER: "[Mediation] Résumé du dossier",
  PLEADING_BRIEF: "Dossier de plaidoirie",
  PLEADING_BRIEF_LETTER: "Lettre au président du tribunal",
  OPERATING_CARRIER: "Transporteur effectif",
  EMAIL_CONVERSATION: "Conversation email",
  PROOF_OF_SEPA_TRANSFER: "Preuve de virement SEPA",
  PAYMENT_RECEIPT: "Quittance de paiment",
  MEDIATION_DECISION: "[Mediation] Avis du médiateur",
  MEDIATION_PROOF: "Preuve de médiation",
  MEDIATION_STARTED_PROOF: "Preuve d'envoi en médiation",
};

/** Restricted list shown to agents (non Upclaim / LAWYER / PLEADING_LAWYER). */
export const typeForAgent: { [key in ClaimDocumentType]?: string } = {
  DEBT_CONTRACT: "Signature Passager",
  ID_PROOF: "Carte(s) d'identité",
  ETICKET: "E-ticket(s)",
  ELIGIBILITY_PROOF: "Preuve d'annulation / retard",
  PASSENGER_DECLARATION: "Déclaration passager",
  ONE_PAGER: "Résumé du dossier",
  INVOICES_MERGED: "Factures",
  DEBT_CONTRACT_ENTERPRISE: "Cession de créance Entreprise",
  OTHER: "Autre",
};

/** Port of utils/config/constants/procedure-document-types.ts (Vue) + seed display texts. */
export const procedureDocumentTypesMap: {
  [key in ProcedureDocumentType]: { icon: LucideIcon; label: string };
} = {
  SUMMONS: { icon: Calendar1Icon, label: "Convocation" },
  RESPONSE_CONCLUSIONS: { icon: MessageSquareShareIcon, label: "Conclusions en réponse (nous)" },
  DEFENSE_CONCLUSIONS: { icon: MessageSquareDotIcon, label: "Conclusions en défense (adversaire)" },
  LETTER: { icon: MailIcon, label: "Courrier" },
  SUBPOENA: { icon: PinIcon, label: "Assignation signifiée" },
  NOTICE: { icon: MailWarningIcon, label: "Avis" },
  NOTIFICATION: { icon: MailIcon, label: "Notification" },
  STATEMENT_OF_CLAIM: { icon: PinIcon, label: "Saisine (REPL)" },
  NOTICE_OF_APPEARANCE_FOR_DEFENSE: { icon: FileUser, label: "Fiche de constitution" },
  COURT_DECISION: { icon: GavelIcon, label: "Décision du tribunal" },
  JUDGEMENT_OF_DISMISSAL: { icon: GavelIcon, label: "Jugement de désistement" },
  CONCLUSION: { icon: MessageSquareText, label: "Conclusion (non spécifiée)" },
};

const EXT_COLORS: Record<string, { bg: string; text: string }> = {
  pdf: { bg: "#fde8e8", text: "#c81e1e" },
  doc: { bg: "#e1effe", text: "#1e40af" },
  docx: { bg: "#e1effe", text: "#1e40af" },
  xls: { bg: "#def7ec", text: "#03724c" },
  xlsx: { bg: "#def7ec", text: "#03724c" },
  csv: { bg: "#def7ec", text: "#03724c" },
  ppt: { bg: "#fef3c7", text: "#b45309" },
  pptx: { bg: "#fef3c7", text: "#b45309" },
  jpg: { bg: "#ede9fe", text: "#6d28d9" },
  jpeg: { bg: "#ede9fe", text: "#6d28d9" },
  png: { bg: "#ede9fe", text: "#6d28d9" },
  gif: { bg: "#ede9fe", text: "#6d28d9" },
  zip: { bg: "#f3f4f6", text: "#4b5563" },
  rar: { bg: "#f3f4f6", text: "#4b5563" },
  txt: { bg: "#f3f4f6", text: "#6b7280" },
};
const DEFAULT_EXT_COLOR = { bg: "#f3f4f6", text: "#6b7280" };

export function getFileExtension(filename: string): string {
  return filename.split(".").pop()?.toLowerCase() || "";
}

export function getExtColor(filename: string): { bg: string; text: string } {
  return EXT_COLORS[getFileExtension(filename)] || DEFAULT_EXT_COLOR;
}

export interface DocumentGroup {
  type: string;
  label: string;
  documents: ClaimDocument[];
}

/** Mirrors ClaimDocuments.groupedDocuments (Vue) — singles first, multi-doc groups last. */
export function groupDocuments(documents: ClaimDocument[]): DocumentGroup[] {
  const groups = new Map<string, DocumentGroup>();

  for (const doc of documents) {
    const type = doc.documentType.type;
    const existing = groups.get(type);
    if (existing) {
      existing.documents.push(doc);
    } else {
      groups.set(type, {
        type,
        label: typeTranslations[type] || type,
        documents: [doc],
      });
    }
  }

  return Array.from(groups.values()).sort((a, b) => {
    if (a.documents.length === 1 && b.documents.length > 1) {
      return -1;
    }
    if (a.documents.length > 1 && b.documents.length === 1) {
      return 1;
    }
    return 0;
  });
}

/** Mirrors ClaimDocuments.filteredGroups (Vue) — matches file name or group label. */
export function filterDocumentGroups(groups: DocumentGroup[], query: string): DocumentGroup[] {
  const q = query.toLowerCase().trim();
  if (!q) {
    return groups;
  }

  const filtered: DocumentGroup[] = [];
  for (const group of groups) {
    const matchingDocs = group.documents.filter(
      (doc) => (doc.file.name ?? "").toLowerCase().includes(q) || group.label.toLowerCase().includes(q),
    );
    if (matchingDocs.length) {
      filtered.push({ ...group, documents: matchingDocs });
    }
  }
  return filtered;
}
